/**
 * Tenant Configuration Service
 *
 * Loads tenant-specific configuration from backend API before Vue app initialization.
 * Provides singleton access to tenant branding/theme and to the feature list the
 * backend registry (config/features.php) resolved for this tenant (disabled_features).
 *
 * CRITICAL: Must be loaded BEFORE creating Vue app in main.js
 */

import { reactive } from 'vue'
import { detectTenantSubdomain, getCurrentHostname } from '../utils/tenantDetection'
import { log } from '../utils/DebugLogger'
import { config } from './configService'
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from '../theme/colors'
import { getContrastingTextColor } from '../theme/colorUtils'

export interface TenantThemeConfig {
  primary_color?: string
  secondary_color?: string
  logo_url?: string
  logo_alt?: string
  favicon_url?: string
  app_title?: string
  [key: string]: unknown
}

export interface TenantInfo {
  id: string | null
  name: string | null
  code: string | null
}

export interface TenantState {
  isLoaded: boolean
  isLoading: boolean
  error: string | null
  config: TenantThemeConfig | null
  tenantId: string | null
  tenantName: string | null
  tenantCode: string | null
  /** Feature keys disabled for this tenant — config/features.php resolved by the backend. */
  disabledFeatures: string[]
}

/** Waits before each new try when tenant_config fails without a 500 (network, 502–504). */
const TENANT_CONFIG_RETRY_DELAYS_MS = [500, 1500]

interface CriticalError extends Error {
  isCritical?: boolean
  statusCode?: number
}

interface RGB {
  r: number
  g: number
  b: number
}

class TenantConfigService {
  state: TenantState
  subdomain: string | null
  hostname: string

  constructor() {
    this.state = reactive<TenantState>({
      isLoaded: false,
      isLoading: false,
      error: null,
      config: null,
      disabledFeatures: [],
      tenantId: null,
      tenantName: null,
      tenantCode: null
    })

    this.subdomain = detectTenantSubdomain()
    this.hostname = getCurrentHostname()

    log('TenantConfigService', 'constructor', 'Initialized with subdomain:', this.subdomain)
  }

  async loadConfig(attempt = 0): Promise<TenantThemeConfig | null> {
    if (this.state.isLoaded) {
      log('TenantConfigService', 'loadConfig', 'Config already loaded, skipping')
      return this.state.config
    }

    if (this.state.isLoading && attempt === 0) {
      log('TenantConfigService', 'loadConfig', 'Config loading in progress, waiting...')
      return this._waitForLoad()
    }

    this.state.isLoading = true
    this.state.error = null

    log('TenantConfigService', 'loadConfig', 'Loading config for hostname:', this.hostname)

    try {
      const tenantConfigHost = config.api.tenantConfigHost
      const apiUrl = tenantConfigHost
        ? `${tenantConfigHost}/api/tenant_config/config`
        : '/api/tenant_config/config'

      log('TenantConfigService', 'loadConfig', 'Fetching from URL:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      })

      if (!response.ok) {
        if (response.status === 500) {
          const error: CriticalError = new Error(`Erro crítico do servidor (HTTP 500): Não foi possível carregar a configuração do tenant`)
          error.isCritical = true
          error.statusCode = 500
          throw error
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.message || 'Failed to load tenant config')
      }

      this.state.tenantId = data.data.tenant_id
      this.state.tenantName = data.data.tenant_name
      this.state.tenantCode = data.data.tenant_code
      this.state.config = data.data.config
      // The feature registry (backend config/features.php) resolved for THIS
      // tenant: env opt-out ∪ features whose domain gate is off. configService
      // is seeded from it in bootstrap/tenant.ts. A payload without the field
      // means an api older than the registry — log it loudly, treat as "all on".
      if (Array.isArray(data.data.disabled_features)) {
        this.state.disabledFeatures = data.data.disabled_features
      } else {
        this.state.disabledFeatures = []
        console.error('[TenantConfigService] tenant_config payload has no disabled_features — api predates the feature registry; every feature is ON')
      }
      this.state.isLoaded = true

      log('TenantConfigService', 'loadConfig', 'Config loaded successfully:', {
        tenantId: this.state.tenantId,
        tenantName: this.state.tenantName,
        configKeys: Object.keys(this.state.config || {})
      })

      this._applyConfig()

      return this.state.config

    } catch (rawError) {
      const error = rawError as CriticalError
      log('TenantConfigService', 'loadConfig', 'Failed to load config:', error)
      this.state.error = error.message

      const transient = !(error.isCritical && error.statusCode === 500)
      if (transient && attempt < TENANT_CONFIG_RETRY_DELAYS_MS.length) {
        await new Promise(resolve => setTimeout(resolve, TENANT_CONFIG_RETRY_DELAYS_MS[attempt]))
        return this.loadConfig(attempt + 1)
      }
      this.state.isLoading = false
      if (!transient) throw error

      // Fail closed: booting on a made-up config turned every module on for a
      // tenant that has some of them off (DS 2026-0070). Stop with "reload".
      const critical: CriticalError = new Error(`Não foi possível carregar a configuração do tenant: ${error.message}`)
      critical.isCritical = true
      critical.statusCode = 0
      throw critical
    }
  }

  private async _waitForLoad(): Promise<TenantThemeConfig | null> {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (!this.state.isLoading) {
          clearInterval(checkInterval)
          resolve(this.state.config)
        }
      }, 50)
    })
  }

  private _hexToRgb(hex: string): string {
    hex = hex.replace(/^#/, '')
    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return `${r}, ${g}, ${b}`
  }

  private _hexToRgbObject(hex: string): RGB {
    hex = hex.replace(/^#/, '')
    const bigint = parseInt(hex, 16)
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255
    }
  }

  private _rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b]
      .map(x => {
        const hex = Math.round(x).toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')
  }

  private _mixColors(color1: RGB, color2: RGB, amount: number): RGB {
    return {
      r: color1.r + (color2.r - color1.r) * amount,
      g: color1.g + (color2.g - color1.g) * amount,
      b: color1.b + (color2.b - color1.b) * amount
    }
  }

  private _darken(hex: string, amount: number): string {
    const rgb = this._hexToRgbObject(hex)
    const black: RGB = { r: 0, g: 0, b: 0 }
    const mixed = this._mixColors(rgb, black, amount)
    return this._rgbToHex(mixed.r, mixed.g, mixed.b)
  }

  private _lighten(hex: string, amount: number): string {
    const rgb = this._hexToRgbObject(hex)
    const white: RGB = { r: 255, g: 255, b: 255 }
    const mixed = this._mixColors(rgb, white, amount)
    return this._rgbToHex(mixed.r, mixed.g, mixed.b)
  }

  private _applyConfig(): void {
    const cfg = this.state.config
    if (!cfg) return

    log('TenantConfigService', '_applyConfig', 'Applying configuration...', cfg)

    if (cfg.primary_color) {
      document.documentElement.style.setProperty('--tenant-primary-color', cfg.primary_color)
      const primaryRgb = this._hexToRgb(cfg.primary_color)
      document.documentElement.style.setProperty('--app-primary-color-rgb', primaryRgb)
      document.documentElement.style.setProperty('--app-primary-contrast-color', getContrastingTextColor(cfg.primary_color))
      log('TenantConfigService', '_applyConfig', 'Applied primary color:', cfg.primary_color, 'RGB:', primaryRgb)
    }

    if (cfg.secondary_color) {
      document.documentElement.style.setProperty('--tenant-secondary-color', cfg.secondary_color)
      const secondaryRgb = this._hexToRgb(cfg.secondary_color)
      document.documentElement.style.setProperty('--app-secondary-color-rgb', secondaryRgb)

      const secondaryHover = this._darken(cfg.secondary_color, 0.1)
      const secondaryActive = this._darken(cfg.secondary_color, 0.15)
      const secondaryLight = this._lighten(cfg.secondary_color, 0.95)

      document.documentElement.style.setProperty('--tenant-secondary-hover-color', secondaryHover)
      document.documentElement.style.setProperty('--tenant-secondary-active-color', secondaryActive)
      document.documentElement.style.setProperty('--tenant-secondary-light-color', secondaryLight)

      log('TenantConfigService', '_applyConfig', 'Applied secondary color:', cfg.secondary_color, 'RGB:', secondaryRgb)
      log('TenantConfigService', '_applyConfig', 'Applied secondary shades:', {
        hover: secondaryHover,
        active: secondaryActive,
        light: secondaryLight
      })
    }

    if (cfg.favicon_url) {
      let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement | null

      if (!favicon) {
        favicon = document.createElement('link')
        favicon.rel = 'icon'
        favicon.type = 'image/x-icon'
        document.head.appendChild(favicon)
        log('TenantConfigService', '_applyConfig', 'Created favicon link element')
      }

      favicon.href = cfg.favicon_url
      log('TenantConfigService', '_applyConfig', 'Applied favicon:', cfg.favicon_url)
    }

    if (cfg.app_title) {
      document.title = cfg.app_title
      log('TenantConfigService', '_applyConfig', 'Applied title:', cfg.app_title)
    }

    log('TenantConfigService', '_applyConfig', 'Configuration applied successfully')
  }

  /** Feature keys the backend disabled for this tenant (lowercase). */
  getDisabledFeatures(): string[] {
    return this.state.disabledFeatures
  }

  getLogo(): string {
    return this.state.config?.logo_url || '/default-logo.png'
  }

  getLogoAlt(): string {
    return this.state.config?.logo_alt || 'Sistema'
  }

  getTheme(): { primaryColor: string; secondaryColor: string } {
    return {
      primaryColor: this.state.config?.primary_color || DEFAULT_PRIMARY_COLOR,
      secondaryColor: this.state.config?.secondary_color || DEFAULT_SECONDARY_COLOR
    }
  }

  get<T = unknown>(key: string, defaultValue: T | null = null): T | null {
    const value = this.state.config?.[key]
    return (value as T | undefined) ?? defaultValue
  }

  isLoaded(): boolean {
    return this.state.isLoaded
  }

  getTenantInfo(): TenantInfo {
    return {
      id: this.state.tenantId,
      name: this.state.tenantName,
      code: this.state.tenantCode
    }
  }
}

export const tenantConfig = new TenantConfigService()
export { TenantConfigService }
export default tenantConfig
